import { Autocomplete, Box, FormControl, InputLabel, Select, Stack, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useHistory } from 'react-router';
import { Prompt } from 'react-router';

import { CREATE, DataAPIContext } from '../api';
import AccessTagsDropdown from '../components/AccessTagsDropdown';
import FixedLinearProgress from '../components/FixedLinearProgress';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext from '../components/FormLicenseeContext';
import LicenseeFormField from '../components/LicenseeFormField';
import PageMeta from '../components/PageMeta';
import { ContextStore } from '../store/contextStore';
import { disableMuiButton, enableMuiButton } from '../utils';
import { CARD_ARRANGEMENT_HORIZONTAL, cardArrangementTypesMenuItems } from './utils';

const useStyles = makeStyles((theme) => ({
    root: {
        boxSizing: 'border-box',
        width: '100%',
        maxWidth: '100%',
        margin: 0,
        paddingTop: theme.spacing(2),
        paddingLeft: 0,
        paddingRight: 0,
        paddingBottom: 0,
        minHeight: `calc(${theme.cp.contentHeight} - 1rem)`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
    },
    gridRoot: {
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        scrollbarGutter: 'stable',
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        boxSizing: 'border-box',
        [theme.breakpoints.down('sm')]: {
            paddingBottom: theme.spacing(12),
        },
    },
    formFooterDocked: {
        flexShrink: 0,
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        paddingTop: 0,
        paddingBottom: 0,
        marginBottom: 0,
        backgroundColor: 'transparent',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'nowrap',
        boxSizing: 'border-box',
        width: '100%',
        maxWidth: '100%',
        [theme.breakpoints.down('sm')]: {
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            width: 'auto',
            maxWidth: 'none',
            margin: 0,
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
            paddingTop: theme.spacing(1.5),
            paddingBottom: `calc(${theme.spacing(1.5)} + env(safe-area-inset-bottom, 0px))`,
            zIndex: theme.zIndex.appBar,
        },
    },
}));

const validateInput = (region, isFranchise) => {
    const errors = {};
    let canSave = true;
    if (isFranchise && !region.tour_operator) {
        errors.tour_operator = { error: true, helperText: 'Licensee is required' };
        canSave = false;
    }
    if (!region.name || region.name.length <= 0) {
        if (region._changed && region._changed_name) {
            errors.name = { error: true, helperText: 'Region name is required' };
        }
        canSave = false;
    }
    return { canSave, errors };
};

const RegionCreate = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const resource = 'region';
    const history = useHistory();
    const classes = useStyles();
    const saveRef = React.useRef(null);
    const { countries, hasPerm, isFranchise, currentLicensee } = React.useContext(ContextStore);
    const [syncActive, setSyncActive] = React.useState(false);
    const [, setServerErrors] = React.useState([]);
    const { enqueueSnackbar } = useSnackbar();
    const [region, setRegion] = React.useState({
        name: '',
        card_arrangement: CARD_ARRANGEMENT_HORIZONTAL,
        countries: [],
        latitude: '',
        longitude: '',
        tour_operator: currentLicensee || null,
        _changed: false,
    });
    const selectedCountry =
        region.countries && region.countries.length > 0 ? countries.find((c) => c.id === region.countries[0]) ?? null : null;
    const handleChange = (ev) => {
        const cfn = `_changed_${ev.target.name}`;
        setRegion({ ...region, [ev.target.name]: ev.target.value, _changed: true, [cfn]: true });
    };
    const onAccessTagsChange = (ev, atags, reason) => {
        setRegion((prevState) => ({ ...prevState, atag: atags, _changed: true }));
    };
    const handleSelectChange = (ev, country) => {
        country === null
            ? setRegion({ ...region, countries: [], _changed: true, _changed_countries: true })
            : setRegion({ ...region, countries: [country.id], _changed: true, _changed_countries: true });
    };
    const handleSave = (ev) => {
        console.log('save', region);
        setSyncActive(true);
        disableMuiButton(saveRef.current);
        (async () => {
            try {
                const res = await dataAPI(CREATE, resource, { data: region });
                console.log('create', res);
                if (res.statusCode === 201) {
                    enqueueSnackbar('Region created', { variant: 'success' });
                    setRegion({ ...region, _changed: false, _changed_name: false, _changed_countries: false, _changed_card_arrangement: false });
                    history.push(`/explore/regions`);
                } else if (res.statusCode === 406) {
                    enqueueSnackbar('Region validation error', { variant: 'warning' });
                    console.log('validation errors', res.data);
                    setServerErrors(res.data.errors);
                }
            } finally {
                setSyncActive(false);
                if (saveRef && saveRef.current) {
                    enableMuiButton(saveRef.current);
                }
            }
        })();
    };

    const onLicenseeChange = (id) => {
        setRegion((prev) => ({ ...prev, tour_operator: id, _changed: true }));
    };
    const { canSave, errors } = validateInput(region, isFranchise);

    console.log('RegionCreate: render', region);

    return (
        <div className={classes.root}>
            <PageMeta title="Create New Region" />
            <Prompt when={!!region._changed} message="Are you sure you want to leave without saving changes?" />
            <FixedLinearProgress visibility={syncActive} />
            <FormLicenseeContext.Provider value={region.tour_operator}>
            <div className={classes.gridRoot}>
                <Stack spacing={2} sx={{ width: '100%', boxSizing: 'border-box' }}>
                    <Typography variant="h6">Create New Region</Typography>
                    <LicenseeFormField value={region.tour_operator} onChange={onLicenseeChange} error={errors.tour_operator?.error} helperText={errors.tour_operator?.helperText} />
                    <TextField helperText={`${28 - region.name.length} characters left`} {...errors.name} required variant="filled" size="small" fullWidth name="name" label="Name" value={region.name} onChange={handleChange} inputProps={{ maxLength: 28 }} />
                    <FormControl variant="filled" size="small" fullWidth>
                        <InputLabel htmlFor="card-arrangement-select-element">Card Arrangement</InputLabel>
                        <Select value={region.card_arrangement} onChange={handleChange} name="card_arrangement" inputProps={{ name: 'card_arrangement', id: 'card-arrangement-select-element' }}>
                            {cardArrangementTypesMenuItems}
                        </Select>
                    </FormControl>
                    <FormControl variant="filled" size="small" fullWidth>
                        <Autocomplete
                            fullWidth
                            options={countries}
                            onChange={handleSelectChange}
                            noOptionsText={'No matches'}
                            value={selectedCountry}
                            name="countries"
                            getOptionLabel={(option) => option.name}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    name="countries"
                                    label="Choose Country"
                                    variant="filled"
                                    size="small"
                                    fullWidth
                                    inputProps={{ ...params.inputProps, autoComplete: 'new-password' }}
                                />
                            )}
                        />
                    </FormControl>
                    <TextField
                        helperText={`${128 - (region.custom_id?.length || 0)} characters left`}
                        variant="filled"
                        size="small"
                        name="custom_id"
                        label="External ID"
                        fullWidth
                        value={region.custom_id}
                        onChange={handleChange}
                        inputProps={{
                            maxLength: 128,
                        }}
                        FormHelperTextProps={{ sx: { mx: 0 } }}
                    />
                    <AccessTagsDropdown value={region.atag} onChange={onAccessTagsChange} />
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ width: '100%' }}>
                        <TextField label="Latitude" name="latitude" value={region.latitude} onChange={handleChange} variant="filled" size="small" fullWidth sx={{ flex: 1 }} />
                        <TextField label="Longitude" name="longitude" value={region.longitude} onChange={handleChange} variant="filled" size="small" fullWidth sx={{ flex: 1 }} />
                    </Stack>
                </Stack>
            </div>
            <Box className={classes.formFooterDocked}>
                <FormActionButtons
                    submitRef={saveRef}
                    onSubmit={handleSave}
                    submitDisabled={!hasPerm('region', 'write') || !canSave}
                    onCancel={() => history.push('/explore/regions')}
                    size="medium"
                    sx={{ flex: 1 }}
                />
            </Box>
            </FormLicenseeContext.Provider>
        </div>
    );
};

export default RegionCreate;
